import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  PingUser,
  PingUserSearchRequest,
  PingUserSearchResponse,
  PingUserUpdateRequest,
  PingUserUpdateResponse
} from '../models/ping-user.model';

/**
 * Service for interacting with PIAM (PingOne) via the Support Portal backend.
 *
 * This service provides methods to:
 * - Search for users in PingOne by name/DOB
 * - Update user information (MPI, email, phone)
 * - Get user by external ID
 *
 * All calls are proxied through the Support Portal backend, which handles:
 * - Admin JWT generation (secret never exposed to frontend)
 * - PIAM API calls
 * - Audit logging
 *
 * @example
 * // Search for a user
 * this.piamService.searchUsers('John', 'Doe', '1990-01-15').subscribe(response => {
 *   console.log('Found users:', response.users);
 * });
 *
 * @example
 * // Update a user
 * this.piamService.updateUser({
 *   externalId: 'user-123',
 *   mpi: '9999999',
 *   email: 'new@email.com'
 * }).subscribe(response => {
 *   if (response.success) {
 *     console.log('Updated:', response.user);
 *   } else {
 *     console.error('Error:', response.error);
 *   }
 * });
 */
@Injectable({
  providedIn: 'root'
})
export class PiamService {
  private http = inject(HttpClient);

  /**
   * Backend API base URL
   * Frontend makes direct HTTP calls to the Spring Boot backend.
   * CORS is configured on the backend to allow requests from the frontend origin.
   */
  private apiUrl = environment.apiUrl;

  /**
   * Search for users in PingOne by name and DOB.
   *
   * @param firstName Patient's first name
   * @param lastName Patient's last name
   * @param dob Date of birth (YYYY-MM-DD format)
   * @returns Observable with search results
   */
  searchUsers(firstName: string, lastName: string, dob: string): Observable<PingUserSearchResponse> {
    const request: PingUserSearchRequest = { firstName, lastName, dob };

    return this.http.post<PingUserSearchResponse>(
      `${this.apiUrl}/piam/search`,
      request
    ).pipe(
      catchError(error => {
        console.error('PIAM search error:', error);
        return of({
          users: [],
          totalCount: 0,
          error: error.message || 'Search failed'
        } as PingUserSearchResponse);
      })
    );
  }

  /**
   * Update a user in PingOne.
   *
   * This will:
   * - Update the specified fields in PingOne
   * - Update MFA devices if email/phone changes
   * - Send PubSub event for BigQuery sync
   *
   * @param request Update request with externalId and fields to update
   * @returns Observable with update result
   */
  updateUser(request: PingUserUpdateRequest): Observable<PingUserUpdateResponse> {
    return this.http.post<PingUserUpdateResponse>(
      `${this.apiUrl}/piam/update`,
      request
    ).pipe(
      catchError(error => {
        console.error('PIAM update error:', error);
        return of({
          success: false,
          error: error.error?.error || error.message || 'Update failed'
        } as PingUserUpdateResponse);
      })
    );
  }

  /**
   * Get a user by external ID.
   *
   * @param externalId The user's external ID
   * @returns Observable with user data or null if not found
   */
  getUserByExternalId(externalId: string): Observable<PingUser | null> {
    return this.http.get<PingUser>(
      `${this.apiUrl}/piam/user`,
      { params: { externalId } }
    ).pipe(
      catchError(error => {
        console.error('PIAM get user error:', error);
        return of(null);
      })
    );
  }

  /**
   * Search for a PingOne user using patient information.
   *
   * Uses the patient's name and DOB to search PIAM and find
   * the matching PingOne account.
   *
   * @param firstName Patient's first name
   * @param lastName Patient's last name (family name)
   * @param dob Date of birth (YYYY-MM-DD format)
   * @param mpi MPI to match against results
   * @returns Observable with user data or null if not found
   * @deprecated Use searchUsersByPatient instead to get all matching accounts
   */
  searchUserByPatient(firstName: string, lastName: string, dob: string, mpi: string): Observable<PingUser | null> {
    return this.searchUsers(firstName, lastName, dob).pipe(
      switchMap(response => {
        if (response.error) {
          console.error('PIAM search error:', response.error);
          return of(null);
        }

        if (!response.users || response.users.length === 0) {
          return of(null);
        }

        // Try to find user matching the MPI
        const matchingUser = response.users.find(u => u.mpi === mpi);
        if (matchingUser) {
          return of(matchingUser);
        }

        // If no exact MPI match, return first result (name/DOB matched)
        return of(response.users[0]);
      }),
      catchError(error => {
        console.error('PIAM search by patient error:', error);
        return of(null);
      })
    );
  }

  /**
   * Search for ALL PingOne accounts matching the patient's MPI.
   *
   * Uses the patient's name and DOB to search PIAM and returns
   * all accounts that have a matching MPI.
   *
   * @param firstName Patient's first name
   * @param lastName Patient's last name (family name)
   * @param dob Date of birth (YYYY-MM-DD format)
   * @param mpi MPI to filter results
   * @returns Observable with array of matching users (empty if none found)
   */
  searchUsersByPatient(firstName: string, lastName: string, dob: string, mpi: string): Observable<PingUser[]> {
    return this.searchUsers(firstName, lastName, dob).pipe(
      switchMap(response => {
        if (response.error) {
          console.error('PIAM search error:', response.error);
          return of([]);
        }

        if (!response.users || response.users.length === 0) {
          return of([]);
        }

        // Return all users matching the MPI
        const matchingUsers = response.users.filter(u => u.mpi === mpi);
        return of(matchingUsers);
      }),
      catchError(error => {
        console.error('PIAM search by patient error:', error);
        return of([]);
      })
    );
  }
}
